import { observable, makeObservable, computed } from 'mobx'
import React from 'react'
import _debug from 'debug'

import { Passage, PassageGloss, PassageSegment, PassageVideo } from './ProjectModels'
import { fmt, s } from '../components/utils/Fmt'

const log = _debug('sltt:GlossInfo')

/* Holds info about the Gloss label being displayed/edited.
 * This is primarily updated in the GlossEditor component.
 * It is displayed in the RootVideoPlayer and DraftVideoPlayer 
 * components (each of these has its own GlossLabel instance).
 */
export class GlossInfo {
    @observable glossIndex = -1 // index of the gloss in the passageGlosses array, -1 if not editing
    @observable passageGloss: PassageGloss | null = null
    passageVideo: PassageVideo | null = null // the base passageVideo that contains the glosses
    passage: Passage | null = null // the passage that contains the glosses
    @observable text = ''
    @observable position = 0 // position of the gloss in the video, in seconds
    @observable hasLabel = false
    @observable textZoom = 1.0
    @observable xy = [0, 0] // [x, y] position of the gloss label in the video player
    // as a percentage of the video player size, e.g. [0.0, 0.5] is left/center of the video player.

    @observable minPosition = 0
    @observable maxPosition = 0

    /* In the gloss editor, a click outside the editor causes an automatic save/close.
     * This reference allows us to consider a click on the gloss label
     * as a click inside the editor, so that we do not trigger an automatic save
     * when we drag the gloss label. 
     */
    glossLabelRef = React.createRef<HTMLDivElement>()

    constructor() {
        makeObservable(this) // required to make the observable properties work
    }

    setup(passage: Passage | null, passageVideo: PassageVideo | null) {
        log('setup', fmt({ passage, passageVideo }))
        this.passage = passage
        this.passageVideo = passageVideo
    }

    /** A visible gloss is a gloss on an unpatched segment of the base video or a gloss on
     * the most recent patch video for a patched segment.
     * These are the only glosses that can be displayed or edited.
     */
    get visibleGlosses() {
        let { passage, passageVideo } = this
        if (!passage || !passageVideo) {
            log('### visibleGlosses: no passage or passageVideo')
            return []
        }
        // Visible glosses are cached in the PassageVideo
        return passageVideo.getVisibleGlosses(passage)
    }

    /** true iff gloss editing in progress */
    get editing() {
        return this.glossIndex >= 0
    }

    /** Close editor (you must call save() before this if you want to retain any edits that have been done) */
    closeEditor = () => {
        log('closeEditor')
        this.setGlossIndex(-1)
    }

    /** Set the glossIndex to the index of the gloss in the visibleGlosses array.
     * If glossIndex is -1, then close the editor.
     * If glossIndex is >= 0, then set the passageGloss to the corresponding gloss.
     */
    setGlossIndex = (glossIndex: number) => {
        log(`setGlossIndex ${glossIndex}`)
        this.glossIndex = glossIndex
        if (glossIndex >= 0) {
            this.set(this.visibleGlosses[glossIndex] ?? null)
        }
    }

    // If _index is a valid, return the corresponding gloss, video, segment
    getGlossInfo(_index: number) {
        let { visibleGlosses, passage } = this

        let gloss = null
        let video: PassageVideo | null = null
        let segment: PassageSegment | null = null

        if (_index >= 0 && _index < visibleGlosses.length) {
            gloss = visibleGlosses[_index]
            video = (passage && gloss.toVideo(passage)) ?? null
            segment = video?.positionToSegment(gloss.position)?.segment ?? null
        }

        log('getGlossInfo', fmt({ _index, gloss, video, segment }))
        return { gloss, video, segment }
    }

    findGlossIndex = (gloss: PassageGloss | null) => {
        let index = -1
        if (gloss) {
            const { visibleGlosses } = this
            index = visibleGlosses.findIndex(g => g._id === gloss._id)
        }
        
        log('findGlossIndex', fmt({ glossText: gloss?.text, index }))
        return index
    }

    set = (passageGloss: PassageGloss | null) => {
        this.passageGloss = passageGloss

        if (passageGloss === null) {
            log('set: null')
            this.hasLabel = false
            return
        }

        this.text = passageGloss.text
        this.position = passageGloss.position
        const { hasLabel, xy, textZoom } = passageGloss.getGlossLabel()
        this.hasLabel = hasLabel
        this.xy = xy
        this.textZoom = textZoom

        this.setMinMaxPosition()

        this.logDebug('set')
    }

    setMinMaxPosition = () => {
        const MIN_GLOSS_WIDTH = .5 // seconds
        const MAX_GLOSS_WIDTH = 2.0 // seconds

        const index = this.findGlossIndex(this.passageGloss)

        const { segment } = this.getGlossInfo(index)
        const { segment: prevSegment } = this.getGlossInfo(index - 1)
        const { segment: nextSegment, gloss: nextGloss } = this.getGlossInfo(index + 1)

        if (!prevSegment || prevSegment?._id !== segment?._id) {
            // No previous gloss or previous gloss in a different segment
            // Min position is the start of this segment
            this.minPosition = segment?.position ?? 0
        } else {
            // Previous gloss is in the same segment as this gloss.
            // Ensure that previous gloss maintains min width
            this.minPosition = prevSegment.position + MIN_GLOSS_WIDTH
        }

        if (!nextSegment || segment?._id !== nextSegment?._id) {
            // No next gloss or next gloss in a different segment
            // Max position is the end of this segment
            this.maxPosition = segment?.endPosition ?? MAX_GLOSS_WIDTH
        } else {
            // Next gloss is in the same segment as this gloss.
            // Ensure that the gloss ends before start of next gloss
            this.maxPosition = nextGloss?.position ?? MAX_GLOSS_WIDTH
        }

        const { minPosition, maxPosition } = this
        log('setMinMaxPosition', fmt({ minPosition, maxPosition }))
    }

    setPosition = (position: number) => {
        const { minPosition, maxPosition } = this
        this.position = Math.max(minPosition, Math.min(maxPosition, position))
        log(`setPosition: ${this.position} (min: ${minPosition}, max: ${maxPosition})`)
    }

    logDebug = (message?: string) => {
        let { glossIndex, editing, passageGloss, text, position, hasLabel, xy, textZoom } = this
        log(`GlossInfo ${message || ''}`, fmt({ passageGloss, glossIndex, text, position, hasLabel, xy, textZoom }))
    }

    save = async () => {
        const { passageGloss, hasLabel, xy, textZoom, position, text, glossIndex } = this
        this.logDebug('save')
        if (glossIndex < 0) { log('save, but not editing'); return }
        if (passageGloss === null) { log('save, no passageGloss'); return }

        await passageGloss.setGlossLabel(hasLabel, xy, textZoom)
        await passageGloss.set(position, text)
    }

    cancel = () => {
        const { passageGloss, glossIndex } = this

        log('cancel')
        if (glossIndex < 0) { log('cancel: not editing'); return }
        if (passageGloss === null) { log('cancel: no passageGloss'); return }
        
        this.set(passageGloss)
        this.logDebug('cancel')
    }

    setHasLabel = (hasLabel: boolean) => {
        log(`setHasLabel ${hasLabel}/${this.hasLabel}`)
        if (this.hasLabel === hasLabel) { return }
        this.hasLabel = hasLabel
        this.xy = [0, 0]
        this.textZoom = 1.0
    }

    setXy = (xy: number[]) => {
        log(`setXy ${s(xy)}`)
        this.xy = xy
    }

    setTextZoom = (textZoom: number) => {
        log(`setTextZoom ${textZoom}`)
        this.textZoom = textZoom
    }

    setText = (text: string) => {
        log(`setText ${text}`)
        this.text = text
    }

    time = () => {
        const { glossIndex, position } = this
        const { segment } = this.getGlossInfo(glossIndex)

        console.assert(glossIndex >= 0)
        console.assert(segment)
        if (glossIndex < 0 || !segment) return 0

        return segment.positionToTime(position)
    }

    duration = () => {
        const { glossIndex, visibleGlosses, passageVideo } = this
        if (glossIndex < 0 || !passageVideo) return 0
        let endTime = passageVideo.duration
        if (glossIndex + 1 < visibleGlosses.length) {
            endTime = visibleGlosses[glossIndex + 1].time
        }

        return endTime - this.time()
    }
}
